import { DriveEta as DriveEtaIcon } from "@mui/icons-material";
import { Divider, Grid } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { InvoiceAutocomplete } from "components/job/InvoiceAutocomplete";
import TimeBlockComponent from "components/job/times/TimeBlockComponent";
import { TotalTimeComponent } from "components/job/times/TotalTimeComponent";
import PrimaryButton from "components/PrimaryButton";
import { PromptDialog } from "components/PromptDialog";
import SecondaryGreyButton from "components/SecondaryGreyButton";
import { formatTimeDiff, mergeDateTime, toDateString } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { cloneDeep } from "lodash";
import { Times } from "models/travelTimes";
import { JobStatus } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  acceptJob,
  addTravel,
  refreshJob,
  selectSelectedJob,
  selectSelectedJobVisit,
  setTravelTimes,
  setTravelTimesInvoiceType,
  stopTravel,
  validateVisit,
} from "store/slices/partner.store";

export type TravelType = {
  travelTimes: Times[];
};

interface TravelTabProps {
  handleClose?: () => void;
}

export const TravelTab: FC<TravelTabProps> = (props) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const jobVisit = useAppSelector(selectSelectedJobVisit);
  const { travelTimes, travelTimesInvoiceType } = jobVisit;
  const [openDialog, setOpenDialog] = useState(false);
  const [index, setIndex] = useState(0);
  const [isLastOneEnded, setIsLastOneEnded] = useState(true);
  const allowChangeInvoiceType = technicianSettings.allowChangeInvoiceType;

  useEffect(() => {
    if (travelTimes.length === 0) {
      setIsLastOneEnded(true);
    } else {
      setIsLastOneEnded(
        travelTimes[travelTimes.length - 1].stopDate && travelTimes[travelTimes.length - 1].stopTime
          ? true
          : false,
      );
    }
  }, [travelTimes]);

  const dispatchUpdate = useCallback(
    (times: Times[]) => {
      dispatch(setTravelTimes({ times: cloneDeep(times) }));
    },
    [dispatch],
  );

  const timeBlockOnChange = useCallback(
    (id: number, fieldname: "startDate" | "startTime" | "stopDate" | "stopTime", value: Date) => {
      let newTimes = [...cloneDeep(travelTimes)];
      newTimes[id][fieldname] = toDateString(value);
      dispatchUpdate(newTimes);
    },
    [dispatchUpdate, travelTimes],
  );

  const onStartTravel = useCallback(async () => {
    await dispatch(addTravel());
    if (travelTimes.length === 0) {
      if (job.status !== JobStatus.Accepted) {
        dispatch(
          acceptJob({
            jobId: job.id,
          }),
        )
          .then(unwrapResult)
          .then(() => {
            dispatch(refreshJob());
          });
      }
    }
    dispatch(validateVisit());
  }, [dispatch, job.id, job.status, travelTimes.length]);

  const onStopTravel = useCallback(async () => {
    await dispatch(stopTravel());
    dispatch(validateVisit());
    props.handleClose?.();
  }, [dispatch, props]);

  const deleteTravelTime = useCallback(
    (index: number) => {
      const newArray = travelTimes.filter((time) => time !== travelTimes[index]);
      dispatchUpdate(newArray);
    },
    [dispatchUpdate, travelTimes],
  );

  const timeInvoiceOnChange = useCallback(
    async (id: number, value: string) => {
      let newTimes = cloneDeep(travelTimes);
      newTimes[id]["invoiceType"] = value;
      dispatchUpdate(newTimes);
    },
    [dispatchUpdate, travelTimes],
  );

  return (
    <>
      <Grid container direction="column" spacing={1} width="100%" pl={1} pr={1}>
        {travelTimes.map((times, index) => {
          return (
            <div key={`travel-${index}`} style={{ paddingLeft: "8px" }}>
              {index > 0 && <Divider variant="middle" component="div" sx={{ margin: 2 }} />}
              {times.startTime !== null && (
                <>
                  <Grid container item marginBottom={1}>
                    <TimeBlockComponent
                      jobVisit={jobVisit}
                      setOpenDeleteDialog={setOpenDialog}
                      setIndex={setIndex}
                      timeBlockOnChange={timeBlockOnChange}
                      id={index}
                      showLastOne={!(!isLastOneEnded && travelTimes.length - 1 === index)}
                      timesLabel="travelTimes"
                      timesText={intl.formatMessage({
                        id: "times.travelTime",
                      })}
                      disabled={
                        times.uploaded || times.pendingUpload || !technicianSettings.allowEditTimes
                      }
                      warning={!times.uploaded}
                      totalTime={
                        times.stopTime && times.stopDate
                          ? formatTimeDiff(
                              intl,
                              Math.floor(
                                (mergeDateTime(times.stopDate, times.stopTime).getTime() -
                                  mergeDateTime(times.startDate, times.startTime).getTime()) /
                                  1000 /
                                  60,
                              ),
                            )
                          : ""
                      }
                    />
                  </Grid>
                  {allowChangeInvoiceType && (
                    <Grid container item marginBottom={1}>
                      <InvoiceAutocomplete
                        onChange={(value: string) => {
                          timeInvoiceOnChange(index, value);
                        }}
                        value={times.invoiceType ?? null}
                      />
                    </Grid>
                  )}
                </>
              )}
            </div>
          );
        })}

        <Grid
          item
          container
          justifyContent="space-between"
          direction="column"
          width="100%"
          spacing={1}
          wrap="nowrap"
          sx={{
            mt: 1,
            ml: 0,
            pl: "0 !important",
          }}
        ></Grid>
        <Grid item xs={12} ml={1}>
          <TotalTimeComponent times={travelTimes} />
        </Grid>

        {isLastOneEnded || !travelTimes[0]?.startTime ? (
          <>
            <Grid item xs={12}>
              <PrimaryButton fullWidth startIcon={<DriveEtaIcon />} onClick={onStartTravel}>
                {intl.formatMessage({ id: "visit.startTravel" })}
              </PrimaryButton>
            </Grid>
            {allowChangeInvoiceType && (
              <Grid item container xs={12}>
                <InvoiceAutocomplete
                  onChange={(value: string) => {
                    dispatch(setTravelTimesInvoiceType({ invoiceType: value }));
                  }}
                  value={travelTimesInvoiceType ?? null}
                />
              </Grid>
            )}
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <SecondaryGreyButton onClick={() => deleteTravelTime(travelTimes.length - 1)}>
                {intl.formatMessage({ id: "general.cancel" })}
              </SecondaryGreyButton>
            </Grid>
            <Grid item xs={12}>
              <PrimaryButton startIcon={<DriveEtaIcon />} onClick={onStopTravel}>
                {intl.formatMessage({ id: "visit.stopTravel" })}
              </PrimaryButton>
            </Grid>
          </>
        )}
      </Grid>
      <PromptDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onOk={() => {
          deleteTravelTime(index);
        }}
        promptContent={<FormattedMessage id="times.deletePrompt" />}
        title={intl.formatMessage({ id: "visit.deleteTimesPrompt" })}
        okText={intl.formatMessage({ id: "general.continue" })}
      />
    </>
  );
};
